import React from 'react';
import PropTypes from 'prop-types';
import { Box, Checkbox, FormControl, Input, ListItemText, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { BORDERLESS_SELECT_SX, SelectChevronIcon, WHITE_PILL_SEGMENTED_TOGGLE_SX } from './control_language_styles';
import { THEMES, DEFAULT_THEME_KEY } from './likert_section';

const ALL_SCHOOLS_VALUE = '__ALL_SCHOOLS_ORG_WIDE__';
const ALL_ROLES_VALUE = '__ALL_ROLES__';

const stripShellStyles = {
  borderRadius: 0,
  backgroundColor: 'transparent',
  px: { xs: 1.0, md: 1.15 },
  py: { xs: 0.95, md: 1.05 },
};

const fieldLabelStyles = {
  fontWeight: 400,
  color: '#64748b',
  fontSize: '0.9rem',
  lineHeight: 1.2,
};

const selectStyles = {
  width: 'auto',
  minWidth: 0,
  ...BORDERLESS_SELECT_SX,
};

const THEME_LABELS = {
  original: 'Original',
  refined_blue: 'Refined Blue',
  warm_cool: 'Warm Cool',
  soft_diverging: 'Soft Diverging',
  care: 'CARE',
  tol_blue_red: 'Tol Scientific',
  sunset: 'Tol Spectral',
  us_federal: 'US Federal',
  earth_tones: 'Earth Tones',
};

const THEME_OPTIONS = Object.keys(THEMES).map((key) => ({ value: key, label: THEME_LABELS[key] || key }));

const normalizeSelection = (value) => (Array.isArray(value) ? value : []);

const toCompactSummary = (selectedValues) => {
  const normalized = Array.isArray(selectedValues) ? selectedValues : [];
  if (normalized.length === 0) {
    return 'All';
  }
  if (normalized.length === 1) {
    return normalized[0];
  }
  return `${normalized.length} selected`;
};

const normalizeSelectChangeValue = (value, currentSelection = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    if (parsed.length <= 1) {
      const [nextItem] = parsed;
      if (!nextItem) {
        return [];
      }
      return currentSelection.includes(nextItem) ? currentSelection.filter((item) => item !== nextItem) : [...currentSelection, nextItem];
    }

    return parsed;
  }

  return [];
};

const FilterBarBespoke2026 = ({ filters, onChange, surveysFilters, sortOrder, onSortOrderChange, isSuperadmin, themeKey, onThemeKeyChange }) => {
  const schoolOptions = Array.isArray(surveysFilters?.schools) ? surveysFilters.schools : [];
  const roleOptions = Array.isArray(surveysFilters?.roles) ? surveysFilters.roles : [];
  const selectedSchool = normalizeSelection(filters.school);
  const selectedRole = normalizeSelection(filters.role);
  const selectedGroupBy = filters.group_by || 'none';
  const allSchoolsSelected = selectedSchool.length === 0;
  const allRolesSelected = selectedRole.length === 0;
  const hasSchoolOptions = schoolOptions.length > 0;
  const hasRoleOptions = roleOptions.length > 0;

  const handleSchoolChange = (event) => {
    const nextValue = normalizeSelectChangeValue(event.target.value, selectedSchool);
    if (nextValue.includes(ALL_SCHOOLS_VALUE)) {
      onChange({
        school: [],
      });
      return;
    }

    onChange({
      school: nextValue,
    });
  };

  const handleRoleChange = (event) => {
    const nextValue = normalizeSelectChangeValue(event.target.value, selectedRole);
    if (nextValue.includes(ALL_ROLES_VALUE)) {
      onChange({
        role: [],
      });
      return;
    }

    onChange({
      role: nextValue,
    });
  };

  const handleGroupByChange = (_, nextValue) => {
    if (!nextValue) {
      return;
    }

    onChange({
      group_by: nextValue,
    });
  };

  const handleSortOrderChange = (event) => {
    onSortOrderChange(event.target.value);
  };

  return (
    <Box sx={stripShellStyles}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.0,
          rowGap: 0.45,
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ gap: '10px', mr: { xs: 0.75, md: 1.15 } }}>
          <Typography variant="body1" sx={fieldLabelStyles}>
            School
          </Typography>
          <FormControl size="small" variant="standard" sx={selectStyles}>
            <Select
              value={selectedSchool}
              onChange={handleSchoolChange}
              multiple
              displayEmpty
              aria-label="School filter"
              renderValue={toCompactSummary}
              disabled={!hasSchoolOptions}
              variant="standard"
              input={<Input disableUnderline />}
              IconComponent={SelectChevronIcon}
              MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
            >
              <MenuItem value={ALL_SCHOOLS_VALUE}>
                <Checkbox size="small" checked={allSchoolsSelected} />
                <ListItemText primary="All Schools" />
              </MenuItem>
              {schoolOptions.map((school) => (
                <MenuItem key={school} value={school}>
                  <Checkbox size="small" checked={selectedSchool.includes(school)} />
                  <ListItemText primary={school} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
          <Typography variant="body1" sx={fieldLabelStyles}>
            Role
          </Typography>
          <FormControl size="small" variant="standard" sx={selectStyles}>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              multiple
              displayEmpty
              aria-label="Role Detail filter"
              renderValue={toCompactSummary}
              disabled={!hasRoleOptions}
              variant="standard"
              input={<Input disableUnderline />}
              IconComponent={SelectChevronIcon}
              MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
            >
              <MenuItem value={ALL_ROLES_VALUE}>
                <Checkbox size="small" checked={allRolesSelected} />
                <ListItemText primary="All Roles" />
              </MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  <Checkbox size="small" checked={selectedRole.includes(role)} />
                  <ListItemText primary={role} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Box
          sx={{
            width: '1px',
            height: 24,
            backgroundColor: '#e2e8f0',
            display: { xs: 'none', md: 'block' },
            mx: { md: 0.65, lg: 0.85 },
          }}
        />
        <Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
          <Typography variant="body1" sx={fieldLabelStyles}>
            Group
          </Typography>
          <ToggleButtonGroup value={selectedGroupBy} exclusive size="small" onChange={handleGroupByChange} sx={WHITE_PILL_SEGMENTED_TOGGLE_SX}>
            <ToggleButton value="none" disableRipple>
              None
            </ToggleButton>
            <ToggleButton value="school" disableRipple>
              School
            </ToggleButton>
            <ToggleButton value="role" disableRipple>
              Role
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box
          sx={{
            width: '1px',
            height: 24,
            backgroundColor: '#e2e8f0',
            display: { xs: 'none', md: 'block' },
            mx: { md: 0.65, lg: 0.85 },
          }}
        />
        <Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
          <Typography variant="body1" sx={fieldLabelStyles}>
            Sort
          </Typography>
          <FormControl size="small" variant="standard" sx={selectStyles}>
            <Select
              value={sortOrder}
              onChange={handleSortOrderChange}
              aria-label="Sort order"
              variant="standard"
              input={<Input disableUnderline />}
              IconComponent={SelectChevronIcon}
            >
              <MenuItem value="default">A-Z</MenuItem>
              <MenuItem value="desc">{'High\u2009\u2192\u2009Low'}</MenuItem>
              <MenuItem value="asc">{'Low\u2009\u2192\u2009High'}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {isSuperadmin && (
          <>
            <Box
              sx={{
                width: '1px',
                height: 24,
                backgroundColor: '#e2e8f0',
                display: { xs: 'none', md: 'block' },
                mx: { md: 0.65, lg: 0.85 },
              }}
            />
            <Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
              <Typography variant="body1" sx={fieldLabelStyles}>
                Theme
              </Typography>
              <FormControl size="small" variant="standard" sx={selectStyles}>
                <Select
                  value={themeKey}
                  onChange={(event) => onThemeKeyChange(event.target.value)}
                  aria-label="Theme"
                  variant="standard"
                  input={<Input disableUnderline />}
                  IconComponent={SelectChevronIcon}
                >
                  {THEME_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

FilterBarBespoke2026.propTypes = {
  filters: PropTypes.shape({
    survey_id: PropTypes.string,
    school: PropTypes.arrayOf(PropTypes.string),
    role: PropTypes.arrayOf(PropTypes.string),
    group_by: PropTypes.oneOf(['none', 'school', 'role']),
  }).isRequired,
  surveysFilters: PropTypes.shape({
    schools: PropTypes.arrayOf(PropTypes.string),
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  onChange: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['default', 'desc', 'asc']),
  onSortOrderChange: PropTypes.func,
  isSuperadmin: PropTypes.bool,
  themeKey: PropTypes.string,
  onThemeKeyChange: PropTypes.func,
};

FilterBarBespoke2026.defaultProps = {
  surveysFilters: {
    schools: [],
    roles: [],
  },
  sortOrder: 'default',
  onSortOrderChange: () => {},
  isSuperadmin: false,
  themeKey: DEFAULT_THEME_KEY,
  onThemeKeyChange: () => {},
};

export default FilterBarBespoke2026;
